import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Import types
import {
    Product,
    Category,
    Establishment,
    CartItem,
    ActiveOrder,
    CartItemCustomizations,
    SelectedOptionPick
} from '../types/menu';

// Import config
import { API_URL, getImageUrl } from '../config/api';

// Import helpers
import { getStatusColor, getStatusLabel, formatTime } from '../utils/menuHelpers';

// Import components
import { CartModal, ProductModal, CustomizationOverlay, BuildYourOwnSheet } from '../components/modals';
import ProductCard from '../components/menu/ProductCard';
import PromotionBanner from '../components/PromotionBanner';
import { MenuLoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { useToast } from '../components/ui/Toast';

interface Promotion {
    id: number;
    name: string;
    description: string;
    type: 'percentage' | 'fixed_amount';
    value: number;
    applies_to: 'product' | 'category' | 'all';
    products?: { id: number; name?: string }[];
    categories?: { id: number; name?: string }[];
}

const MenuPage: React.FC = () => {
    const { establishmentId, tableId } = useParams<{ establishmentId: string; tableId?: string }>();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [establishment, setEstablishment] = useState<Establishment | null>(null);
    const [tableName, setTableName] = useState<string>('');
    const [categories, setCategories] = useState<Category[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
    const [cart, setCart] = useState<CartItem[]>([]);
    const [showCart, setShowCart] = useState(false);
    const [activeOrders, setActiveOrders] = useState<ActiveOrder[]>([]);
    const [showActiveOrders, setShowActiveOrders] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [showCustomization, setShowCustomization] = useState(false);
    const [customizingProduct, setCustomizingProduct] = useState<Product | null>(null);
    const [buildYourOwnProduct, setBuildYourOwnProduct] = useState<Product | null>(null);
    const [loading, setLoading] = useState(true);
    const [isMenuViewOnly, setIsMenuViewOnly] = useState(false); // Track if this is view-only mode
    const [subscriptionExpired, setSubscriptionExpired] = useState(false); // Track subscription status

    // Generate or retrieve session ID for scan tracking
    const getSessionId = (): string => {
        let sessionId = sessionStorage.getItem('qr_session_id');
        if (!sessionId) {
            sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
            sessionStorage.setItem('qr_session_id', sessionId);
        }
        return sessionId;
    };

    // Track QR code scan
    const trackScan = async () => {
        if (!tableId) return; // Don't track if no table (view-only mode)
        try {
            await axios.post(`${API_URL}/public/track-scan`, {
                tableId,
                sessionId: getSessionId()
            });
        } catch (error) {
            // Don't interrupt user experience if tracking fails
            console.debug('Scan tracking skipped');
        }
    };

    useEffect(() => {
        fetchMenu();
        fetchPromotions(); // Fetch active promotions
        if (tableId) {
            trackScan();
            fetchActiveOrders();
            // Auto-refresh active orders every 15 seconds (only if table exists)
            const interval = setInterval(fetchActiveOrders, 15000);
            return () => clearInterval(interval);
        } else {
            setIsMenuViewOnly(true); // Set view-only mode when no table
        }
    }, [establishmentId, tableId]);

    const fetchMenu = async () => {
        try {
            setLoading(true);
            let response;

            // Check if tableId exists - if yes, use table-specific route, otherwise use establishment route
            if (tableId) {
                response = await axios.get(`${API_URL}/public/menu/${establishmentId}/table/${tableId}`);
                const { table, establishment, categories, products, subscription } = response.data;
                setTableName(table.name);
                setEstablishment(establishment);
                setCategories(categories);
                setProducts(products);
                if (establishment?.name) {
                    document.title = `${establishment.name} — Digital Menu`;
                }
                document.body.dataset.theme = establishment?.client_theme || 'royal-dark';
                // Check subscription status
                if (subscription?.expired) {
                    setSubscriptionExpired(true);
                }
            } else {
                // Direct establishment menu view (no table)
                response = await axios.get(`${API_URL}/public/menu/${establishmentId}`);
                const { establishment, categories, products, subscription } = response.data;
                setTableName(''); // No table in view-only mode
                setEstablishment(establishment);
                setCategories(categories);
                setProducts(products);
                if (establishment?.name) {
                    document.title = `${establishment.name} — Digital Menu`;
                }
                document.body.dataset.theme = establishment?.client_theme || 'royal-dark';
                // Check subscription status
                if (subscription?.expired) {
                    setSubscriptionExpired(true);
                }
            }

            // Set first category as default
            if (response.data.categories.length > 0) {
                setSelectedCategory(response.data.categories[0].id);
            }
        } catch (error) {
            console.error('Error fetching menu:', error);
            // Check for 403 status using axios error structure
            if (axios.isAxiosError(error) && error.response?.status === 403) {
                showToast('Menu is not available for public viewing at this establishment.', 'error');
            } else {
                showToast('Failed to load menu. Please try again.', 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchActiveOrders = async () => {
        if (!tableId) return; // Skip if no table
        try {
            const response = await axios.get(`${API_URL}/public/table/${tableId}/active-orders`);
            setActiveOrders(response.data);
        } catch (error) {
            console.error('Error fetching active orders:', error);
        }
    };

    const fetchPromotions = async () => {
        if (!establishmentId) return;
        try {
            const response = await axios.get(`${API_URL}/public/${establishmentId}/promotions/active`);
            setPromotions(response.data);
        } catch (error) {
            console.debug('No active promotions or error fetching promotions:', error);
        }
    };

    // Helper function to get promotion for a specific product
    const getPromotionForProduct = useCallback((product: Product): Promotion | undefined => {
        if (promotions.length === 0) return undefined;

        // Find promotions that apply to this product, ordered by priority
        const applicablePromotions = promotions.filter((promo) => {
            if (promo.applies_to === 'all') return true;
            if (promo.applies_to === 'product' && promo.products) {
                return promo.products.some(p => p.id === product.id);
            }
            if (promo.applies_to === 'category' && promo.categories) {
                return promo.categories.some(c => c.id === product.category_id);
            }
            return false;
        });

        // Return the first (highest priority) applicable promotion
        return applicablePromotions.length > 0 ? applicablePromotions[0] : undefined;
    }, [promotions]);

    // Memoize filtered products to prevent recalculation on every render
    const filteredProducts = useMemo(() =>
        selectedCategory
            ? products.filter(p => p.category_id === selectedCategory)
            : products,
        [selectedCategory, products]
    );

    // Handle promotion banner click – navigate to the relevant category or product
    const handlePromotionClick = useCallback((promo: Promotion) => {
        if (promo.applies_to === 'category' && promo.categories && promo.categories.length > 0) {
            // Switch to the first category in the promotion
            const targetCatId = promo.categories[0].id;
            const catExists = categories.find(c => c.id === targetCatId);
            if (catExists) {
                setSelectedCategory(targetCatId);
                // Scroll the category bar into view
                setTimeout(() => {
                    const catBar = document.getElementById('category-bar');
                    if (catBar) catBar.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }, 100);
            }
        } else if (promo.applies_to === 'product' && promo.products && promo.products.length > 0) {
            // Find the product in our loaded products list
            const targetProductId = promo.products[0].id;
            const targetProduct = products.find(p => p.id === targetProductId);
            if (targetProduct) {
                // Switch to its category first
                setSelectedCategory(targetProduct.category_id);
                // Then open the product detail modal after a short delay for the category to render
                setTimeout(() => {
                    setSelectedProduct(targetProduct);
                }, 150);
            }
        } else if (promo.applies_to === 'all') {
            // For "all" promotions, just scroll to the products area
            const catBar = document.getElementById('category-bar');
            if (catBar) catBar.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [categories, products]);

    // Memoized cart functions to prevent unnecessary re-renders in child components
    const addToCart = useCallback((product: Product, customizations?: {
        removed_ingredients?: string[];
        selected_accompaniment?: string;
        special_notes?: string;
    }) => {
        // Build-Your-Own products go through the wizard
        if (product.is_customizable) {
            setBuildYourOwnProduct(product);
            return;
        }

        // If product has customization and no customizations provided, show overlay
        if (product.has_customization && !customizations) {
            setCustomizingProduct(product);
            setShowCustomization(true);
            return;
        }

        setCart(prevCart => {
            const existingItem = prevCart.find(
                item => item.product_id === product.id
                    && !item.customizations
                    && !(item.selectedOptions && item.selectedOptions.length > 0)
            );

            if (existingItem && !customizations) {
                // Merge into the simple line
                return prevCart.map(item =>
                    item.lineId === existingItem.lineId
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            } else {
                // Add new line (with or without customizations)
                return [...prevCart, {
                    lineId: `line_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
                    product_id: product.id,
                    name: product.name,
                    price: Number(product.price),
                    quantity: 1,
                    image_url: product.image_url,
                    customizations: customizations
                }];
            }
        });
    }, []);

    const handleBuildYourOwnConfirm = useCallback((payload: {
        quantity: number;
        notes: string;
        selectedOptions: SelectedOptionPick[];
        extraPricePerUnit: number;
    }) => {
        if (!buildYourOwnProduct) return;
        setCart(prevCart => [
            ...prevCart,
            {
                lineId: `line_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
                product_id: buildYourOwnProduct.id,
                name: buildYourOwnProduct.name,
                price: Number(buildYourOwnProduct.price),
                quantity: payload.quantity,
                image_url: buildYourOwnProduct.image_url,
                customizations: payload.notes
                    ? { special_notes: payload.notes }
                    : undefined,
                selectedOptions: payload.selectedOptions,
                extraPricePerUnit: payload.extraPricePerUnit,
            },
        ]);
        setBuildYourOwnProduct(null);
    }, [buildYourOwnProduct]);

    const removeFromCart = useCallback((lineId: string) => {
        setCart(prevCart => prevCart.filter(item => item.lineId !== lineId));
    }, []);

    const updateQuantity = useCallback((lineId: string, quantity: number) => {
        if (quantity <= 0) {
            setCart(prevCart => prevCart.filter(item => item.lineId !== lineId));
        } else {
            setCart(prevCart => prevCart.map(item =>
                item.lineId === lineId
                    ? { ...item, quantity }
                    : item
            ));
        }
    }, []);

    const cartTotal = cart.reduce(
        (sum, item) => sum + ((item.price + Number(item.extraPricePerUnit || 0)) * item.quantity),
        0
    );
    const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

    if (loading) {
        return <MenuLoadingSkeleton />;
    }

    // Show subscription expired overlay
    if (subscriptionExpired) {
        return (
            <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
                textAlign: 'center'
            }}>
                {/* Animated warning icon */}
                <div style={{
                    width: '120px',
                    height: '120px',
                    background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '2rem',
                    boxShadow: '0 0 60px rgba(220, 38, 38, 0.4)',
                    animation: 'pulse 2s ease-in-out infinite'
                }}>
                    <svg
                        width="60"
                        height="60"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                </div>

                {/* Establishment logo if available */}
                {establishment?.logo_url && (
                    <img
                        src={getImageUrl(establishment.logo_url) || ''}
                        alt={establishment.name}
                        style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'contain',
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '16px',
                            padding: '0.75rem',
                            marginBottom: '1.5rem',
                            opacity: 0.7
                        }}
                    />
                )}

                {/* Main message */}
                <h1 style={{
                    color: 'white',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    marginBottom: '1rem',
                    lineHeight: 1.3
                }}>
                    Menu Temporarily Unavailable
                </h1>

                <p style={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '1.125rem',
                    marginBottom: '2rem',
                    maxWidth: '400px',
                    lineHeight: 1.6
                }}>
                    The subscription for <strong style={{ color: 'white' }}>{establishment?.name || 'this establishment'}</strong> has expired.
                    Please contact the restaurant staff or management for assistance.
                </p>

                {/* Info box */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    maxWidth: '400px',
                    width: '100%'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        marginBottom: '1rem'
                    }}>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fbbf24"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                        <span style={{ color: '#fbbf24', fontWeight: '600' }}>
                            What does this mean?
                        </span>
                    </div>
                    <p style={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        margin: 0
                    }}>
                        The digital menu service for this establishment needs to be renewed.
                        Ordering and menu viewing are temporarily disabled until the subscription is reactivated.
                    </p>
                </div>

                {/* Decorative elements */}
                <div style={{
                    position: 'absolute',
                    top: '20%',
                    left: '10%',
                    width: '200px',
                    height: '200px',
                    background: 'radial-gradient(circle, rgba(220, 38, 38, 0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    pointerEvents: 'none'
                }} />
                <div style={{
                    position: 'absolute',
                    bottom: '20%',
                    right: '10%',
                    width: '150px',
                    height: '150px',
                    background: 'radial-gradient(circle, rgba(220, 38, 38, 0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    pointerEvents: 'none'
                }} />

                {/* CSS Animation */}
                <style>{`
                    @keyframes pulse {
                        0%, 100% { transform: scale(1); box-shadow: 0 0 60px rgba(220, 38, 38, 0.4); }
                        50% { transform: scale(1.05); box-shadow: 0 0 80px rgba(220, 38, 38, 0.6); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="menu-page" style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
            paddingBottom: '100px'
        }}>
            {/* Header with Logo */}
            <header className="menu-header" style={{
                position: 'sticky',
                top: 0,
                zIndex: 50,
                padding: '1rem',
                background: 'rgba(0, 0, 0, 0.8)',
                backdropFilter: 'blur(20px)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Logo */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {establishment?.logo_url ? (
                            <img
                                src={getImageUrl(establishment.logo_url) || ''}
                                alt={establishment.name}
                                style={{
                                    width: '50px',
                                    height: '50px',
                                    objectFit: 'contain',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    borderRadius: '12px',
                                    padding: '0.5rem'
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '50px',
                                height: '50px',
                                background: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.5rem'
                            }}>
                                🍽️
                            </div>
                        )}
                        <div>
                            <h1 style={{ color: 'white', fontSize: '1.25rem', fontWeight: 'bold' }}>
                                {establishment?.name || 'Menu'}
                            </h1>
                            {tableName && (
                                <div style={{
                                    background: 'rgba(139, 0, 0, 0.3)',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '0.5rem',
                                    display: 'inline-block',
                                    marginTop: '0.25rem'
                                }}>
                                    <p style={{ color: 'white', fontSize: '1rem', fontWeight: 'bold', margin: 0 }}>
                                        {tableName}
                                    </p>
                                </div>
                            )}
                            {isMenuViewOnly && (
                                <div style={{
                                    background: 'rgba(255, 215, 0, 0.2)',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '0.5rem',
                                    display: 'inline-block',
                                    marginTop: '0.25rem',
                                    border: '1px solid rgba(255, 215, 0, 0.3)'
                                }}>
                                    <p style={{ color: '#FFD700', fontSize: '0.875rem', margin: 0 }}>
                                        📖 View Only Mode
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Cart Button - Only show if not in view-only mode */}
                    {!isMenuViewOnly && (
                        <button
                            onClick={() => setShowCart(true)}
                            className="menu-cart-btn"
                            style={{
                                position: 'relative',
                                background: 'rgba(139, 0, 0, 0.8)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                padding: '0.75rem 1.25rem',
                                fontSize: '1rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                transition: 'all 0.3s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(139, 0, 0, 1)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'rgba(139, 0, 0, 0.8)'}
                        >
                            🛒 Cart
                            {cartCount > 0 && (
                                <span style={{
                                    position: 'absolute',
                                    top: '-8px',
                                    right: '-8px',
                                    background: '#ff0000',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold'
                                }}>
                                    {cartCount}
                                </span>
                            )}
                        </button>
                    )}
                </div>
            </header>

            {/* Promotion Banner */}
            {establishmentId && (
                <PromotionBanner
                    establishmentId={establishmentId}
                    onPromotionClick={handlePromotionClick}
                />
            )}

            {/* Active Orders Section (Collapsible) */}
            {activeOrders.length > 0 && (
                <div style={{
                    margin: '1rem',
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    overflow: 'hidden'
                }}>
                    {/* Header */}
                    <button
                        onClick={() => setShowActiveOrders(!showActiveOrders)}
                        style={{
                            width: '100%',
                            background: 'rgba(139, 0, 0, 0.2)',
                            border: 'none',
                            padding: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            transition: 'all 0.3s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(139, 0, 0, 0.3)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'rgba(139, 0, 0, 0.2)'}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <span style={{ fontSize: '1.5rem' }}>🍽️</span>
                            <div style={{ textAlign: 'left' }}>
                                <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold', margin: 0 }}>
                                    Active Orders
                                </h3>
                                <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
                                    {activeOrders.length} order{activeOrders.length !== 1 ? 's' : ''} in progress
                                </p>
                            </div>
                        </div>
                        <span style={{
                            color: 'white',
                            fontSize: '1.5rem',
                            transform: showActiveOrders ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s'
                        }}>
                            ▼
                        </span>
                    </button>

                    {/* Orders List */}
                    {showActiveOrders && (
                        <div style={{ padding: '1rem' }}>
                            {activeOrders.map((order) => (
                                <div key={order.id} style={{
                                    background: 'rgba(0, 0, 0, 0.3)',
                                    borderRadius: '8px',
                                    padding: '1rem',
                                    marginBottom: '0.75rem',
                                    border: '1px solid rgba(255, 255, 255, 0.05)'
                                }}>
                                    {/* Order Header */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'start',
                                        marginBottom: '0.75rem'
                                    }}>
                                        <div>
                                            <h4 style={{ color: 'white', fontSize: '1rem', fontWeight: 'bold', margin: 0 }}>
                                                Order #{order.id}
                                            </h4>
                                            {order.customer_name && (
                                                <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
                                                    {order.customer_name}
                                                </p>
                                            )}
                                            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem', margin: '0.25rem 0 0 0' }}>
                                                {formatTime(order.created_at)}
                                            </p>
                                        </div>
                                        <span style={{
                                            background: getStatusColor(order.status),
                                            color: 'white',
                                            padding: '0.4rem 0.75rem',
                                            borderRadius: '6px',
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                            textTransform: 'uppercase'
                                        }}>
                                            {getStatusLabel(order.status)}
                                        </span>
                                    </div>

                                    {/* Order Items */}
                                    <div style={{ marginBottom: '0.75rem' }}>
                                        {order.items.map((item) => (
                                            <div key={item.id} style={{
                                                display: 'flex',
                                                gap: '0.75rem',
                                                padding: '0.5rem',
                                                background: 'rgba(255, 255, 255, 0.02)',
                                                borderRadius: '6px',
                                                marginBottom: '0.5rem'
                                            }}>
                                                {/* Product Image */}
                                                {item.image_url ? (
                                                    <img
                                                        src={getImageUrl(item.image_url) || ''}
                                                        alt={item.product_name}
                                                        loading="lazy"
                                                        style={{
                                                            width: '50px',
                                                            height: '50px',
                                                            objectFit: 'cover',
                                                            borderRadius: '6px',
                                                            background: 'rgba(255, 255, 255, 0.05)'
                                                        }}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        width: '50px',
                                                        height: '50px',
                                                        background: 'rgba(255, 255, 255, 0.05)',
                                                        borderRadius: '6px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '1.5rem'
                                                    }}>
                                                        🍽️
                                                    </div>
                                                )}

                                                {/* Item Details */}
                                                <div style={{ flex: 1 }}>
                                                    <p style={{ color: 'white', fontSize: '0.95rem', fontWeight: '500', margin: 0 }}>
                                                        {item.product_name}
                                                    </p>
                                                    <p style={{ color: '#FFD700', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
                                                        Qty: {item.quantity} × {establishment?.currency || 'RWF'} {item.price.toLocaleString()}
                                                    </p>
                                                </div>

                                                {/* Item Total */}
                                                <div style={{ textAlign: 'right' }}>
                                                    <p style={{ color: '#FFD700', fontSize: '0.95rem', fontWeight: 'bold', margin: 0 }}>
                                                        {establishment?.currency || 'RWF'} {(item.quantity * item.price).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Special Instructions */}
                                    {order.notes && (
                                        <div style={{
                                            background: 'rgba(255, 200, 100, 0.1)',
                                            border: '1px solid rgba(255, 200, 100, 0.3)',
                                            borderRadius: '6px',
                                            padding: '0.5rem 0.75rem',
                                            marginBottom: '0.75rem'
                                        }}>
                                            <p style={{ color: 'rgba(255, 200, 100, 1)', fontSize: '0.875rem', margin: 0, fontStyle: 'italic' }}>
                                                📝 {order.notes}
                                            </p>
                                        </div>
                                    )}

                                    {/* Order Total */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        paddingTop: '0.75rem',
                                        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                                    }}>
                                        <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.95rem', fontWeight: 'bold' }}>
                                            Total:
                                        </span>
                                        <span style={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                            {establishment?.currency || 'RWF'} {order.total_amount.toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Category Tabs */}
            <div id="category-bar" className="menu-categories" style={{
                position: 'sticky',
                top: '82px',
                zIndex: 40,
                background: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(10px)',
                padding: '1rem',
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
                <div style={{ display: 'inline-flex', gap: '0.75rem' }}>
                    {categories.map(category => (
                        <button
                            key={category.id}
                            onClick={() => setSelectedCategory(category.id)}
                            style={{
                                padding: '0.75rem 1.5rem',
                                borderRadius: '12px',
                                border: 'none',
                                fontSize: '1rem',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'all 0.3s',
                                background: selectedCategory === category.id
                                    ? 'rgba(139, 0, 0, 0.8)'
                                    : 'rgba(255, 255, 255, 0.05)',
                                color: selectedCategory === category.id ? 'white' : 'rgba(255, 255, 255, 0.7)',
                                borderBottom: selectedCategory === category.id
                                    ? '2px solid rgba(139, 0, 0, 1)'
                                    : '2px solid transparent'
                            }}
                        >
                            {category.name}
                        </button>
                    ))}
                </div>
            </div>

            {/* Products List */}
            <div style={{ padding: '1rem' }}>
                {filteredProducts.length === 0 ? (
                    <div className="glass" style={{
                        padding: '3rem',
                        borderRadius: '1rem',
                        textAlign: 'center',
                        color: 'rgba(255, 255, 255, 0.5)'
                    }}>
                        No products available in this category
                    </div>
                ) : (
                    <div className="products-list" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {filteredProducts.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                currency={establishment?.currency || 'RWF'}
                                isMenuViewOnly={isMenuViewOnly}
                                onSelect={setSelectedProduct}
                                onAddToCart={addToCart}
                                promotion={getPromotionForProduct(product)}
                                theme={establishment?.client_theme || 'royal-dark'}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Cart Modal */}
            {showCart && (
                <CartModal
                    cart={cart}
                    establishment={establishment}
                    tableId={tableId}
                    onClose={() => setShowCart(false)}
                    onUpdateQuantity={updateQuantity}
                    onRemove={removeFromCart}
                />
            )}

            {/* Build-Your-Own Wizard */}
            {buildYourOwnProduct && (
                <BuildYourOwnSheet
                    product={buildYourOwnProduct}
                    currency={establishment?.currency || 'RWF'}
                    onClose={() => setBuildYourOwnProduct(null)}
                    onConfirm={handleBuildYourOwnConfirm}
                />
            )}

            {/* Customization Overlay */}
            {showCustomization && customizingProduct && (
                <CustomizationOverlay
                    product={customizingProduct}
                    currency={establishment?.currency || 'RWF'}
                    onClose={() => {
                        setShowCustomization(false);
                        setCustomizingProduct(null);
                    }}
                    onAddToCart={(customizations) => {
                        addToCart(customizingProduct, customizations);
                        setShowCustomization(false);
                        setCustomizingProduct(null);
                    }}
                />
            )}

            {/* Product Detail Modal */}
            {selectedProduct && (
                <ProductModal
                    product={selectedProduct}
                    currency={establishment?.currency || 'RWF'}
                    isMenuViewOnly={isMenuViewOnly}
                    onClose={() => setSelectedProduct(null)}
                    onAddToCart={() => {
                        addToCart(selectedProduct);
                        setSelectedProduct(null);
                    }}
                />
            )}
        </div>
    );
};

export default MenuPage;
